import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { PublicConfigurationContext } from "../Context";
import { ChatEvent, useChatChannel } from "./ChatChannel";

type AICoachState = {
  enabled: boolean,
  active: boolean,
  hasAcceptedTerms: boolean,
  setHasAcceptedTerms: (v: AICoachState["hasAcceptedTerms"]) => void,
  modality: "text" | "audio",
  setModality: (v: AICoachState["modality"]) => void,
  audioElement: HTMLAudioElement | null,
  setAudioElement: (v: AICoachState["audioElement"]) => void,
  sendMessage: (msg: string) => void,
  transcript: ChatEvent[]
};

const defaults: AICoachState = {
  enabled: false,
  active: false,
  hasAcceptedTerms: false,
  setHasAcceptedTerms: () => { },
  modality: "text",
  setModality: () => { },
  audioElement: null,
  setAudioElement: () => { },
  sendMessage: () => { },
  transcript: []
}

export const AICoachContext = React.createContext<AICoachState>(defaults);

export function AICoachProvider({ children }: PropsWithChildren) {
  const chat = useChatChannel();
  const config = useContext(PublicConfigurationContext);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<AICoachState["hasAcceptedTerms"]>(false);
  const [modality, setModality] = useState<AICoachState["modality"]>("text");
  const [audioElement, setAudioElement] = useState<AICoachState["audioElement"]>(null);

  useEffect(() => {
    chat.updateSession({
      modalities: modality === 'text' ? ['text'] : ['text', 'audio']
    });
  }, [modality]);

  useEffect(() => {
    const isEnabled = !!config?.capabilities?.aiCoach && !config?.capabilities?.aiCoach?.disabled;
    chat.setEnabled(hasAcceptedTerms && isEnabled);
  }, [hasAcceptedTerms, config]);

  // if we have both an audio element and a peer connection
  // bind the audio to the peer connection
  useEffect(() => {
    function onAudio(e: RTCTrackEvent) {
      if (audioElement) {
        audioElement.srcObject = e.streams[0];
      }
    }
    chat.manageAudioSubscription('subscribe', onAudio);
    return () => {
      chat.manageAudioSubscription('unsubscribe', onAudio);
      if (audioElement) {
        audioElement.pause();
        audioElement.srcObject = null;
      }
    }
  }, [audioElement, chat.manageAudioSubscription]);

  const stateValue: AICoachState = useMemo(() => ({
    enabled: !!config.experimental?.enableAICoach,
    active: chat.enabled && chat.status.status === 'open',
    hasAcceptedTerms, setHasAcceptedTerms,
    modality, setModality,
    audioElement, setAudioElement,
    sendMessage: chat.sendChatMessage.bind(null, 'user'),
    transcript: chat.messages
  }), [chat, config]);

  return <AICoachContext.Provider value={stateValue}>
    {children}
  </AICoachContext.Provider>;
}

// sugar
export function useAICoach(): AICoachState {
  const AICoach = useContext(AICoachContext);
  return AICoach;
}
